import React, { useState } from "react";
import {
  Accessibility,
  Bluetooth,
  Check,
  ChevronRight,
  Clock,
  Gamepad2,
  Globe,
  Laptop,
  LayoutGrid,
  Lock,
  Monitor,
  Paintbrush,
  RefreshCw,
  Search,
  UserRound,
  Volume2,
  Wifi,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { ACCENTS, WALLPAPERS, useSystem } from "../system";
import { PROFILE } from "../data";
import "./settings.css";

const SECTIONS = [
  { id: "system", label: "System", icon: Laptop },
  { id: "bluetooth", label: "Bluetooth & devices", icon: Bluetooth },
  { id: "network", label: "Network & internet", icon: Wifi },
  { id: "personalisation", label: "Personalisation", icon: Paintbrush },
  { id: "apps", label: "Apps", icon: LayoutGrid },
  { id: "accounts", label: "Accounts", icon: UserRound },
  { id: "time", label: "Time & language", icon: Clock },
  { id: "gaming", label: "Gaming", icon: Gamepad2 },
  { id: "accessibility", label: "Accessibility", icon: Accessibility },
  { id: "privacy", label: "Privacy & security", icon: Lock },
  { id: "update", label: "Windows Update", icon: RefreshCw },
];

const SPECS = [
  ["Device name", "EARLPAPPI-PC"],
  ["Processor", "Web Runtime x64 @ 60 fps"],
  ["Installed RAM", "16.0 GB (usable: as much as your tab allows)"],
  ["Device ID", "9F2C-4A81-77E3-B0D5"],
  ["System type", "64-bit operating system, browser-based"],
  ["Pen and touch", "Touch support with 10 touch points"],
];

const OS_SPECS = [
  ["Edition", "Windows 11 Pro (replica)"],
  ["Version", "24H2"],
  ["Installed on", "2026-07-29"],
  ["OS build", "26100.1742"],
  ["Experience", "Windows Feature Experience Pack 1000.26100"],
];

function Row({ icon: Icon, title, sub, right, onClick, tone }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag className="set-row" onClick={onClick} type={onClick ? "button" : undefined}>
      {Icon && (
        <span className="set-row__icon" style={{ "--tone": tone }}>
          <Icon size={17} />
        </span>
      )}
      <span className="set-row__body">
        <span className="set-row__title">{title}</span>
        {sub && <span className="set-row__sub">{sub}</span>}
      </span>
      {right ?? <ChevronRight size={16} className="set-row__chev" />}
    </Tag>
  );
}

function Personalisation() {
  const { isDark, toggleTheme } = useTheme();
  const { accent, setAccent, wallpaper, setWallpaper } = useSystem();

  return (
    <>
      <h2 className="set-h1">Personalisation</h2>

      <div className="set-preview">
        <div
          className="set-preview__screen"
          style={{ background: isDark ? wallpaper.dark : wallpaper.light }}
        >
          <div className="set-preview__window">
            <span className="set-preview__bar" style={{ background: accent }} />
          </div>
          <div className="set-preview__taskbar" />
        </div>
      </div>

      <h3 className="set-h2">Background</h3>
      <div className="set-wallpapers">
        {WALLPAPERS.map((wp) => (
          <button
            key={wp.id}
            className={`set-wallpaper ${
              wp.id === wallpaper.id ? "set-wallpaper--on" : ""
            }`}
            onClick={() => setWallpaper(wp.id)}
            style={{ background: isDark ? wp.dark : wp.light }}
            aria-label={wp.name}
          >
            {wp.id === wallpaper.id && (
              <span className="set-wallpaper__tick">
                <Check size={14} />
              </span>
            )}
            <span className="set-wallpaper__name">{wp.name}</span>
          </button>
        ))}
      </div>

      <h3 className="set-h2">Colours</h3>
      <Row
        icon={Monitor}
        title="Choose your mode"
        sub="Changes the colours that appear in Windows and apps"
        tone="#0078d4"
        right={
          <div className="set-seg">
            <button
              className={!isDark ? "set-seg__on" : ""}
              onClick={() => isDark && toggleTheme()}
            >
              Light
            </button>
            <button
              className={isDark ? "set-seg__on" : ""}
              onClick={() => !isDark && toggleTheme()}
            >
              Dark
            </button>
          </div>
        }
      />

      <div className="set-card">
        <div className="set-row__title" style={{ marginBottom: 12 }}>
          Accent colour
        </div>
        <div className="set-swatches">
          {ACCENTS.map((c) => (
            <button
              key={c.value}
              className={`set-swatch ${
                c.value.toLowerCase() === accent.toLowerCase()
                  ? "set-swatch--on"
                  : ""
              }`}
              style={{ background: c.value }}
              onClick={() => setAccent(c.value)}
              aria-label={c.name}
              title={c.name}
            >
              {c.value.toLowerCase() === accent.toLowerCase() && (
                <Check size={15} />
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function SystemPage() {
  return (
    <>
      <h2 className="set-h1">System</h2>
      <Row icon={Monitor} title="Display" sub="Monitors, brightness, night light" tone="#0078d4" />
      <Row icon={Volume2} title="Sound" sub="Volume levels, output, input" tone="#8661c5" />
      <Row icon={Globe} title="Power & battery" sub="Sleep, battery usage" tone="#3fa34d" />

      <h3 className="set-h2">About</h3>
      <div className="set-card">
        <div className="set-about">
          <span className="set-about__icon">
            <Laptop size={26} />
          </span>
          <div>
            <div className="set-row__title">EARLPAPPI-PC</div>
            <div className="set-row__sub">Browser-hosted workstation</div>
          </div>
        </div>
        <dl className="set-specs">
          {SPECS.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      </div>

      <h3 className="set-h2">Windows specifications</h3>
      <div className="set-card">
        <dl className="set-specs">
          {OS_SPECS.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </>
  );
}

function UpdatePage() {
  return (
    <>
      <h2 className="set-h1">Windows Update</h2>
      <div className="set-card set-update">
        <span className="set-update__badge">
          <Check size={20} />
        </span>
        <div>
          <div className="set-row__title">You're up to date</div>
          <div className="set-row__sub">Last checked: today, a moment ago</div>
        </div>
        <button className="win-btn">Check for updates</button>
      </div>
      <Row icon={Clock} title="Update history" sub="12 quality updates installed" tone="#4a5b6b" />
      <Row icon={RefreshCw} title="Advanced options" sub="Delivery optimisation, optional updates" tone="#4a5b6b" />
    </>
  );
}

function AccountsPage() {
  return (
    <>
      <h2 className="set-h1">Accounts</h2>
      <div className="set-card set-account">
        <img src={PROFILE.avatar} alt="" className="set-account__avatar" />
        <div>
          <div className="set-row__title">{PROFILE.name}</div>
          <div className="set-row__sub">{PROFILE.email} · Administrator</div>
        </div>
      </div>
      <Row icon={UserRound} title="Your info" sub="Profile photo, account settings" tone="#0078d4" />
      <Row icon={Lock} title="Sign-in options" sub="Windows Hello, PIN, security key" tone="#c4451c" />
      <Row icon={Globe} title="Email & accounts" sub="Accounts used by apps" tone="#0f7b6c" />
    </>
  );
}

const STUBS = {
  bluetooth: [
    ["Devices", "Mouse, keyboard, pen, audio, displays"],
    ["Printers & scanners", "Preferences, troubleshoot"],
    ["Mobile devices", "Instantly access your phone"],
  ],
  network: [
    ["Wi-Fi", "Connected, secured"],
    ["Ethernet", "Not connected"],
    ["VPN", "Add, connect, manage"],
    ["Proxy", "Proxy server for Wi-Fi and Ethernet"],
  ],
  apps: [
    ["Installed apps", "Uninstall, modify, move"],
    ["Default apps", "Defaults for file and link types"],
    ["Startup", "Apps that run automatically"],
  ],
  time: [
    ["Date & time", "Time zone, automatic clock"],
    ["Language & region", "Windows and app language, formats"],
    ["Typing", "Touch keyboard, text suggestions"],
  ],
  gaming: [
    ["Game Bar", "Controller shortcuts, layouts"],
    ["Captures", "Screenshots and recordings"],
    ["Game Mode", "Optimise your PC for play"],
  ],
  accessibility: [
    ["Text size", "Make text bigger across Windows"],
    ["Visual effects", "Transparency, animation"],
    ["Narrator", "Screen reader"],
  ],
  privacy: [
    ["Windows Security", "Protection from viruses and threats"],
    ["Find my device", "Track your device"],
    ["General", "Advertising ID, local content"],
  ],
};

export default function SettingsApp() {
  const [section, setSection] = useState("personalisation");
  const [query, setQuery] = useState("");
  const active = SECTIONS.find((s) => s.id === section);

  const visible = query
    ? SECTIONS.filter((s) =>
        s.label.toLowerCase().includes(query.trim().toLowerCase())
      )
    : SECTIONS;

  return (
    <div className="set">
      <nav className="set-nav win-scroll">
        <div className="set-nav__user">
          <img src={PROFILE.avatar} alt="" />
          <div>
            <div className="set-nav__name">{PROFILE.name}</div>
            <div className="set-nav__mail">{PROFILE.email}</div>
          </div>
        </div>

        <label className="set-search">
          <Search size={14} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a setting"
            aria-label="Find a setting"
          />
        </label>

        {visible.map((s) => (
          <button
            key={s.id}
            className={`set-nav__item ${
              s.id === section ? "set-nav__item--on" : ""
            }`}
            onClick={() => setSection(s.id)}
          >
            <s.icon size={17} />
            {s.label}
          </button>
        ))}
      </nav>

      <div className="set-body win-scroll">
        {section === "personalisation" && <Personalisation />}
        {section === "system" && <SystemPage />}
        {section === "update" && <UpdatePage />}
        {section === "accounts" && <AccountsPage />}
        {STUBS[section] && (
          <>
            <h2 className="set-h1">{active.label}</h2>
            {STUBS[section].map(([title, sub]) => (
              <Row key={title} icon={active.icon} title={title} sub={sub} tone="#4a5b6b" />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
